// src/pages/commerce/ShopifyProductMapping.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { apiDelete, apiGet, apiPost } from "../../api/client";
import { Button } from "../../components/Button";
import SectionShell, {
  type SectionTab,
} from "../../components/layout/SectionShell";
import { A } from "../../styles/appleClasses";
import ShopifyBillingRequiredPanel from "../../components/shopify/ShopifyBillingRequiredPanel";
import type { ShopifyStatus } from "../../types/shopify";
import { isShopifyBillingRequired } from "../../types/shopify";

const SHOPIFY_TABS: SectionTab[] = [
  { label: "Overview", to: "/commerce/shopify", end: true },
  { label: "Product Mapping", to: "/commerce/shopify/product-mapping" },
  { label: "Sync Issues", to: "/commerce/shopify/sync-issues" },
  { label: "Activity", to: "/commerce/shopify/diagnostics" },
];

type NimosProduct = {
  id: number;
  name: string;
  sku?: string | null;
  barcode?: string | null;
  sell_price?: number | null;
  stock_qty?: number | null;
  is_service?: boolean;
  sync_to_shopify?: boolean;
};

type ShopifyCatalogRow = {
  shopify_product_id: string | number;
  shopify_product_title?: string | null;
  shopify_variant_id: string | number;
  shopify_variant_title?: string | null;
  sku?: string | null;
  barcode?: string | null;
  price?: string | number | null;
};

type ShopifyProductLink = {
  id: number;
  connection_id: number;
  shopify_product_id?: string | null;
  shopify_variant_id: string;
  product_id: number;
  sku_snapshot?: string | null;
  barcode_snapshot?: string | null;
  is_active: boolean;
  match_source?: string | null;
};

type FlatVariant = {
  productId: string;
  productTitle: string;
  variantId: string;
  variantTitle: string;
  sku?: string | null;
  barcode?: string | null;
  price?: string | number | null;
};

const money = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
});

function errorMessage(err: unknown, fallback: string) {
  const anyErr = err as any;
  return (
    anyErr?.response?.data?.detail ||
    anyErr?.message ||
    (typeof err === "string" ? err : fallback)
  );
}

function formatPrice(value: number | string | null | undefined) {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? money.format(n) : "—";
}

function productMeta(product: NimosProduct) {
  return [
    product.sku ? `SKU ${product.sku}` : null,
    product.barcode ? `Barcode ${product.barcode}` : null,
    `Price ${formatPrice(product.sell_price)}`,
    product.is_service ? "Service" : `Stock ${product.stock_qty ?? 0}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

function normalizeCatalogRows(catalog: ShopifyCatalogRow[]): FlatVariant[] {
  return catalog.map((row) => ({
    productId: String(row.shopify_product_id),
    productTitle: row.shopify_product_title || "Untitled Shopify product",
    variantId: String(row.shopify_variant_id),
    variantTitle: row.shopify_variant_title || "Default",
    sku: row.sku,
    barcode: row.barcode,
    price: row.price,
  }));
}

export default function ShopifyProductMapping() {
  const [products, setProducts] = useState<NimosProduct[]>([]);
  const [catalog, setCatalog] = useState<ShopifyCatalogRow[]>([]);
  const [links, setLinks] = useState<ShopifyProductLink[]>([]);
  const [selectedByVariant, setSelectedByVariant] = useState<
    Record<string, string>
  >({});
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [shopifyStatus, setShopifyStatus] = useState<ShopifyStatus | null>(
    null,
  );

  const shopifyBillingRequired = isShopifyBillingRequired(shopifyStatus);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);

    try {
      const status = await apiGet<ShopifyStatus>("/shopify/status");
      setShopifyStatus(status);

      if (isShopifyBillingRequired(status)) {
        setProducts([]);
        setCatalog([]);
        setLinks([]);
        setSelectedByVariant({});
        return;
      }

      const [productRows, catalogRows, linkRows] = await Promise.all([
        apiGet<NimosProduct[]>("/products"),
        apiGet<ShopifyCatalogRow[]>("/shopify/catalog"),
        apiGet<ShopifyProductLink[]>("/shopify/product-links"),
      ]);

      setProducts(productRows);
      setCatalog(catalogRows);
      setLinks(linkRows);
    } catch (e) {
      setErr(errorMessage(e, "Failed to load Shopify product mapping."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeLinks = useMemo(
    () => links.filter((link) => link.is_active),
    [links],
  );
  const linkedVariantIds = useMemo(
    () => new Set(activeLinks.map((link) => String(link.shopify_variant_id))),
    [activeLinks],
  );
  const productsById = useMemo(() => {
    const map = new Map<number, NimosProduct>();
    products.forEach((product) => map.set(Number(product.id), product));
    return map;
  }, [products]);
  const linkableProducts = useMemo(() => products, [products]);
  const unlinkedVariants = useMemo(
    () =>
      normalizeCatalogRows(catalog).filter(
        (variant) => !linkedVariantIds.has(variant.variantId),
      ),
    [catalog, linkedVariantIds],
  );
  const linkedProductIds = useMemo(
    () => new Set(activeLinks.map((link) => Number(link.product_id))),
    [activeLinks],
  );
  const inventoryLinks = useMemo(
    () =>
      activeLinks.filter((link) => {
        const product = productsById.get(Number(link.product_id));
        return !!product;
      }),
    [activeLinks, productsById],
  );

  async function refreshAfterAction(message: string) {
    await load();
    setNotice(message);
  }

  async function linkVariant(variant: FlatVariant) {
    const selected = selectedByVariant[variant.variantId];
    const productId = Number(selected);
    const product = productsById.get(productId);
    if (!product) {
      setErr(
        "Choose a Nimos inventory product before linking this Shopify variant.",
      );
      return;
    }

    setBusyKey(`link-${variant.variantId}`);
    setErr(null);
    setNotice(null);
    try {
      await apiPost("/shopify/product-links", {
        product_id: productId,
        shopify_product_id: variant.productId,
        shopify_variant_id: variant.variantId,
        sku_snapshot: variant.sku || product.sku || null,
        barcode_snapshot: variant.barcode || product.barcode || null,
        match_source: "manual",
      });
      await refreshAfterAction(
        `${variant.productTitle} was linked to ${product.name}. Inventory sync is still off.`,
      );
    } catch (e) {
      setErr(errorMessage(e, "Could not link Shopify variant."));
    } finally {
      setBusyKey(null);
    }
  }

  async function confirmInventorySync(link: ShopifyProductLink) {
    const product = productsById.get(Number(link.product_id));
    const label = product?.name || `product #${link.product_id}`;
    const ok = window.confirm(
      `Enable inventory sync for ${label}? Current Nimos inventory will be pushed to Shopify.`,
    );
    if (!ok) return;

    setBusyKey(`confirm-${link.id}`);
    setErr(null);
    setNotice(null);
    try {
      const result = await apiPost<any>(
        `/shopify/product-links/${link.id}/confirm-inventory-sync`,
        {},
      );

      if (!result?.ok || result?.sync_to_shopify !== true) {
        throw new Error(
          result?.message ||
            result?.reason ||
            "Inventory sync could not be enabled yet.",
        );
      }

      await refreshAfterAction(`Inventory sync was confirmed for ${label}.`);
    } catch (e) {
      setErr(errorMessage(e, "Could not confirm inventory sync."));
    } finally {
      setBusyKey(null);
    }
  }

  async function disableInventorySync(link: ShopifyProductLink) {
    const ok = window.confirm(
      "Disable inventory sync?\n\nNimos will stop updating inventory quantities for this Shopify variant.",
    );
    if (!ok) return;

    setBusyKey(`disable-${link.id}`);
    setErr(null);
    setNotice(null);

    try {
      await apiPost(`/shopify/product-links/${link.id}/disable-sync`, {});
      await refreshAfterAction("Inventory sync was disabled.");
    } catch (e) {
      setErr(errorMessage(e, "Could not disable inventory sync."));
    } finally {
      setBusyKey(null);
    }
  }

  async function unlinkProduct(link: ShopifyProductLink) {
    const product = productsById.get(Number(link.product_id));
    const label = product?.name || `product #${link.product_id}`;

    const ok = window.confirm(
      `Unlink ${label} from this Shopify variant?\n\nInventory sync will remain off.`,
    );
    if (!ok) return;

    setBusyKey(`unlink-${link.id}`);
    setErr(null);
    setNotice(null);

    try {
      await apiDelete(`/shopify/product-links/${link.id}`);
      await refreshAfterAction(`${label} was unlinked from Shopify.`);
    } catch (e) {
      setErr(errorMessage(e, "Could not unlink Shopify variant."));
    } finally {
      setBusyKey(null);
    }
  }

  if (shopifyBillingRequired) {
    return (
      <SectionShell
        title="Shopify"
        tabs={SHOPIFY_TABS}
        tabsAriaLabel="Shopify sections"
        closePath="/commerce/shopify"
      >
        <main className="mx-auto w-full max-w-5xl space-y-4">
          <ShopifyBillingRequiredPanel
            installedAppUrl={shopifyStatus?.installed_app_url}
            shopifyAdminAppUrl={shopifyStatus?.shopify_admin_app_url}
          />
        </main>
      </SectionShell>
    );
  }

  return (
    <SectionShell
      title="Shopify"
      tabs={SHOPIFY_TABS}
      tabsAriaLabel="Shopify sections"
      closePath="/commerce/shopify"
    >
      <main className="mx-auto w-full max-w-5xl space-y-4">
        <div className={`${A.card} p-4 sm:p-5`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className={A.title}>Product Mapping</h2>
              <p className="mt-1 text-[13px] text-gray-600">
                Nimos syncs inventory for mapped Shopify products.
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={load}
              disabled={loading || !!busyKey}
            >
              Refresh
            </Button>
          </div>

          {err ? (
            <div className="mt-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-[13px] text-red-700">
              {err}
            </div>
          ) : null}
          {notice ? (
            <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-700">
              {notice}
            </div>
          ) : null}
        </div>

        <section className={`${A.card} overflow-hidden`}>
          <div className="border-b border-gray-100 px-4 py-4 sm:px-5">
            <h3 className="text-[14px] font-semibold text-gray-800">
              Inventory Sync
            </h3>
            <p className="mt-1 text-[13px] text-gray-600">
              Enable Shopify inventory sync for linked products.
            </p>
          </div>

          <div className="divide-y divide-gray-100">
            {loading ? (
              <div className="px-4 py-6 text-[13px] text-gray-500 sm:px-5">
                Loading links…
              </div>
            ) : inventoryLinks.length === 0 ? (
              <div className="px-4 py-6 text-[13px] text-gray-500 sm:px-5">
                No inventory-tracked product links yet.
              </div>
            ) : (
              inventoryLinks.map((link) => {
                const product = productsById.get(Number(link.product_id));

                const isService = !!product?.is_service;

                const syncEnabled = !!product?.sync_to_shopify;
                return (
                  <div
                    key={link.id}
                    className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-semibold text-gray-800">
                        {product?.name || `Product #${link.product_id}`}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        Shopify variant {link.shopify_variant_id}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                      {!isService ? (
                        <Button
                          variant={syncEnabled ? "secondary" : "primary"}
                          minWidth="150px"
                          onClick={() =>
                            syncEnabled
                              ? disableInventorySync(link)
                              : confirmInventorySync(link)
                          }
                          disabled={!!busyKey}
                        >
                          {syncEnabled
                            ? busyKey === `disable-${link.id}`
                              ? "Disabling…"
                              : "Disable Sync"
                            : busyKey === `confirm-${link.id}`
                              ? "Confirming…"
                              : "Enable sync"}
                        </Button>
                      ) : null}

                      <Button
                        variant="secondary"
                        minWidth="96px"
                        onClick={() => unlinkProduct(link)}
                        disabled={!!busyKey}
                      >
                        {busyKey === `unlink-${link.id}`
                          ? "Unlinking…"
                          : "Unlink"}
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className={`${A.card} overflow-hidden`}>
          <div className="border-b border-gray-100 px-4 py-4 sm:px-5">
            <h3 className="text-[14px] font-semibold text-gray-800">
              Link Existing Shopify Variants
            </h3>
            <p className="mt-1 text-[13px] text-gray-600">
              Link Shopify variants to Nimos inventory products.
            </p>
          </div>

          <div className="divide-y divide-gray-100">
            {loading ? (
              <div className="px-4 py-6 text-[13px] text-gray-500 sm:px-5">
                Loading Shopify catalog…
              </div>
            ) : unlinkedVariants.length === 0 ? (
              <div className="px-4 py-6 text-[13px] text-gray-500 sm:px-5">
                No unlinked Shopify variants found.
              </div>
            ) : (
              unlinkedVariants.map((variant) => (
                <div
                  key={variant.variantId}
                  className="grid gap-3 px-4 py-4 sm:grid-cols-[minmax(0,1fr)_minmax(220px,280px)_auto] sm:items-center sm:px-5"
                >
                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-semibold text-gray-800">
                      {variant.productTitle}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      Variant {variant.variantTitle} · SKU {variant.sku || "—"}{" "}
                      · Barcode {variant.barcode || "—"} · Price{" "}
                      {formatPrice(variant.price)}
                    </div>
                  </div>
                  <select
                    className={A.input}
                    value={selectedByVariant[variant.variantId] || ""}
                    onChange={(event) =>
                      setSelectedByVariant((current) => ({
                        ...current,
                        [variant.variantId]: event.target.value,
                      }))
                    }
                    disabled={!!busyKey}
                  >
                    <option value="">Choose Nimos product…</option>
                    {linkableProducts.map((product) => (
                      <option
                        key={product.id}
                        value={product.id}
                        disabled={linkedProductIds.has(product.id)}
                      >
                        {product.name}
                        {linkedProductIds.has(product.id)
                          ? " (already linked)"
                          : ""}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="secondary"
                    minWidth="96px"
                    onClick={() => linkVariant(variant)}
                    disabled={
                      !selectedByVariant[variant.variantId] || !!busyKey
                    }
                  >
                    {busyKey === `link-${variant.variantId}`
                      ? "Linking…"
                      : "Link"}
                  </Button>
                </div>
              ))
            )}
          </div>
        </section>
      </main>
    </SectionShell>
  );
}
